import "../App.css";
import Footer from "../components/Footer";
import Warning from "../components/Warning";
import React, { useRef, useEffect } from "react";
import { LiquidGlass } from "@liquidglass/react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import TypingBanner from "../components/TypingBanner";

const Home = () => {
  const frameRef = useRef(null);
  const imgRef = useRef(null);
  const shineRef = useRef(null);
  const foilRef = useRef(null);
  const streakRef = useRef(null);
  const glowRef = useRef(null);

  // Lerp state for smooth tilt
  const tilt = useRef({ x: 0, y: 0, tx: 0, ty: 0, running: false });

  const getPoint = e =>
    e.touches?.[0] || e.changedTouches?.[0] || e;

  const startLerp = () => {
    if (tilt.current.running) return;
    tilt.current.running = true;
    const loop = () => {
      const t = tilt.current;
      t.x += (t.tx - t.x) * 0.1;
      t.y += (t.ty - t.y) * 0.1;
      if (imgRef.current) {
        imgRef.current.style.transform = `rotateX(${t.y}deg) rotateY(${t.x}deg)`;
      }
      const done = Math.abs(t.tx - t.x) < 0.01 && Math.abs(t.ty - t.y) < 0.01;
      if (done) {
        t.x = t.tx; t.y = t.ty;
        if (imgRef.current) imgRef.current.style.transform = `rotateX(${t.y}deg) rotateY(${t.x}deg)`;
        t.running = false;
      } else {
        frameRef.current = requestAnimationFrame(loop);
      }
    };
    frameRef.current = requestAnimationFrame(loop);
  };

  // Global cursor glow tracker — zero re-renders
  useEffect(() => {
    const glow = glowRef.current;
    if (!glow) return;

    let raf;
    const onMove = (e) => {
      const pt = e.touches?.[0] || e;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        glow.style.setProperty("--mx", `${pt.clientX}px`);
        glow.style.setProperty("--my", `${pt.clientY}px`);
        glow.style.opacity = "1";
      });
    };
    const onLeave = () => { glow.style.opacity = "0"; };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseleave", onLeave);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Tilt effect without re-rendering the component
  const handleMove = (e) => {
    if (!imgRef.current || !foilRef.current || !shineRef.current || !streakRef.current) return;

    const rect = imgRef.current.getBoundingClientRect();

    const px = Math.max(0, Math.min(1, (e.clientX * 1 - rect.left) / rect.width));
    const py = Math.max(0, Math.min(1, (e.clientY * 1 - rect.top * 1) / rect.height));

    const x = (px - 0.5) * -15;
    const y = (py - 0.5) * 15;

    const distance = Math.sqrt(Math.pow(e.clientX - (rect.left + rect.width / 2), 2) + Math.pow(e.clientY - (rect.top + rect.height / 2), 2));
    const strength = Math.min(Math.pow(distance / 1.0, 1) * 0.001, 0.2);

    // Foil
    const maskMult = 2;
    const mask =
    `radial-gradient(circle at ${px*100}% ${py*100}%,
    rgba(0,0,0,${1*strength*maskMult}) 0%,
    rgba(0,0,0,${.9*strength*maskMult}) 5%,
      rgba(0, 0, 0, ${ .5*strength*maskMult}) 10%,
    rgba(0,0,0,${0*strength*maskMult}) 15%,
    rgba(0,0,0,${0.6*strength*maskMult}) 80%,
    rgba(0,0,0,${0.7*strength*maskMult}) 98%)`;
    foilRef.current.style.maskImage = mask;
    foilRef.current.style.webkitMaskImage = mask;
    foilRef.current.style.visibility = "visible";
    foilRef.current.style.filter = `sepia(1) hue-rotate(${x*15}deg) saturate(${1+strength*2}) brightness(${1+strength*2})`;

    // Shine
    shineRef.current.style.opacity = Math.min(strength+0.01 * 1.2, 0.5);
    shineRef.current.style.background =
      `radial-gradient(circle at ${px * 100}% ${py * 100}%,
      rgba(255,255,255,.75),
      rgba(255,255,255,.25) 20%,
      transparent 60%)`;

    // rotate card via lerp
    tilt.current.tx = x;
    tilt.current.ty = y;
    startLerp();
  };

  const handleMouseLeave = () => {
    if (!imgRef.current || !foilRef.current || !shineRef.current || !streakRef.current) return;
    foilRef.current.style.visibility = "hidden";
    shineRef.current.style.opacity = 0.0001;
    streakRef.current.style.opacity = 0.0001;
    // lerp back to flat
    tilt.current.tx = 0;
    tilt.current.ty = 0;
    startLerp();
  };

  return (
    <div className="App" style={{ margin: "-8px" }}>

      {/* ── Entrance & hover styles ── */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(22px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        .enter-name {
          animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.05s;
        }
        .enter-subtitle {
          animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.2s;
        }
        .enter-card {
          animation: fadeUp 0.85s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.38s;
        }
        .enter-buttons {
          animation: fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
          animation-delay: 0.55s;
        }
        .enter-footer-text {
          animation: fadeIn 1s ease both;
          animation-delay: 0.8s;
        }

        /* Nav link hover — sliding underline */
        .nav-link {
          position: relative;
          transition: opacity 0.2s ease;
        }
        .nav-link::after {
          content: '';
          position: absolute;
          left: 0; bottom: -2px;
          width: 0; height: 1px;
          background: currentColor;
          transition: width 0.28s cubic-bezier(0.22, 1, 0.36, 1);
          opacity: 0.6;
        }
        .nav-link:hover::after { width: 100%; }
        .nav-link:hover { opacity: 0.75; }

        /* Contact button hover — lift + glow brighten */
        .contact-btn {
          transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
                      box-shadow 0.22s ease,
                      opacity 0.2s ease;
        }
        .contact-btn:hover {
          transform: translateY(-2px);
          opacity: 0.9;
        }
        .contact-btn:hover .contact-halo {
          opacity: 1 !important;
        }
        .contact-btn:active {
          transform: translateY(0px);
        }
      `}</style>

      {/* ── Cursor glow layer ── */}
      <div
        ref={glowRef}
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          opacity: 0,
          transition: "opacity 0.4s ease",
          background: "radial-gradient(circle 260px at var(--mx, 50%) var(--my, 50%), rgba(180,130,255,0.18) 0%, rgba(100,80,220,0.08) 45%, transparent 75%)",
          mixBlendMode: "screen",
        }}
      />

      <HelmetProvider>
        <Helmet>
          <title>Jack Frederick</title>
          <meta
            name="Jack Frederick, Complete Portfolio"
            content="A fully self made, and self hosted portfolio site."
            data-react-helmet="true"
            description="A fully self made, and self hosted portfolio site."
            author="Jack Frederick"
          />
        </Helmet>
      </HelmetProvider>

      <Warning />

      <header className="Home">
        <div className="screenBox Minsize">
          <p className="Homethick enter-name">Jack Frederick</p>
          <p className="Homemid typeBanner enter-subtitle">
            <TypingBanner />
          </p>
          <br></br>
          <div
            className="imageWrapper enter-card"
            onPointerMove={handleMove}
            onPointerLeave={handleMouseLeave}
            onPointerDown={handleMove}
            style={{ perspective: "1000px" }}
          >
            <div ref={imgRef} className="tiltWrapper">
              <div className="tiltCard">
                <div className="tiltContainer" style={{ boxShadow: "0 0 32px 8px rgba(161, 110, 255, 0.07), 0 0 80px 20px rgba(120,80,220,0.12)" }}>
                  {/* Glow halo behind the image */}
                  <div style={{
                    position: "absolute",
                    inset: "-18px",
                    borderRadius: "inherit",
                    background: "radial-gradient(ellipse at 50% 50%, rgba(160,110,255,0.35) 0%, transparent 70%)",
                    filter: "blur(18px)",
                    zIndex: -1,
                    pointerEvents: "none",
                  }} />
                  <img
                    src="Cover.jpg"
                    alt="Cover"
                    className="coverImg roundCornerImage"
                  />
                  <div className="streakLayer roundCornerImage" id="streakLayer" ref={streakRef}></div>
                  <div className="foilLayer roundCornerImage" ref={foilRef}></div>
                  <div className="shineLayer roundCornerImage" ref={shineRef}></div>
                </div>
              </div>
            </div>

            <div className="buttonBar enter-buttons">
              {/* Glow halo behind the LiquidGlass nav */}
              <div style={{
                position: "absolute",
                inset: "-12px",
                borderRadius: "16px",
                background: "radial-gradient(ellipse at 50% 50%, rgba(131, 100, 255, 0) 0%, transparent 80%)",
                filter: "blur(20px)",
                zIndex: -1,
                pointerEvents: "none",
              }} />

              <LiquidGlass
                className="Glass"
                blur={2}
                saturation={1.1}
                elasticity={0.3}
              >
                <a className="Homesmall spaceme nav-link" href="/resume">
                  Resume and Programming
                </a>
                <a className="Homesmall spaceme nav-link" href="/art">
                  Art and Design
                </a>
                <a className="Homesmall spaceme nav-link" href="/foredark_games">
                  Foredark Games
                </a>
              </LiquidGlass>

              <a className="Homesmall ats spaceme buff contact-btn" href="/contact"
                style={{ position: "relative" }}
              >
                {/* Glow halo behind Contact button */}
                <span className="contact-halo" style={{
                  position: "absolute",
                  inset: "-10px",
                  borderRadius: "12px",
                  background: "radial-gradient(ellipse at 50% 50%, rgba(159, 100, 255, 0) 0%, transparent 90%)",
                  filter: "blur(12px)",
                  zIndex: -1,
                  pointerEvents: "none",
                  opacity: 0.6,
                  transition: "opacity 0.22s ease",
                }} />
                Contact
              </a>
            </div>
          </div>

          <p className="Homesmall enter-footer-text">
            This site is completely hand made, and self hosted by me.
          </p>
          <p className="Homesmall enter-footer-text" style={{ animationDelay: "0.9s" }}>
            You are seeing a preview of the beta version.
          </p>
        </div>
      </header>

      <Footer />
    </div>
  );
};

export default Home;